/**
 * Districts: what a building's *placement* is worth.
 *
 * Production used to depend only on staffing and tier, which meant the correct
 * way to lay out a settlement was a solid block of whatever was best, anywhere.
 * That is not a city; it is a spreadsheet with roofs.
 *
 * A producer is now scored on its neighbourhood, and the score multiplies its
 * output:
 *
 *   Road access   a cart on an old road moves half again as fast, and the
 *                 whole chain feels it. Adjacency to a Road tile pays.
 *   Storage       a warehouse within a short haul means the yard is not
 *                 stacked to the rafters waiting for someone with a barrow.
 *   Chain         a sawmill next to the woodcutters that feed it, a bakery
 *                 next to the fields. Put the converter where its input is.
 *   Crowding      four sawmills in one yard get in each other's way. Past two
 *                 neighbours of the same kind, every extra one costs.
 *
 * That set is deliberately small and legible: every term is something the
 * player can see on the map without opening a panel, and the tooltip names the
 * ones that are firing so a bad placement teaches rather than punishes.
 *
 * Cost. Recomputing is O(buildings²) and would be silly per tick, so it runs
 * only when the layout actually changes — `w.dirty.districts`, set by the same
 * places that set `dirty.jobs`, plus road-laying. The result is a pure function
 * of hashed state and is therefore itself derived: never hashed, never saved,
 * rebuilt on load.
 */

import { MAX_BUILDINGS } from '../constants.ts';
import { RESOURCE_KEYS, Team, Terrain } from '../types.ts';
import type { World } from '../world.ts';
import { buildingType } from '../../data/buildings.ts';

/** How far a haul can be before storage stops helping, in tiles. */
const STORAGE_REACH = 13;
/** How far a converter can be from its supplier and still count as fed. */
const CHAIN_REACH = 15;
/** Same-type neighbours within this radius are counted for crowding. */
const CROWD_REACH = 6.5;

const ROAD_BONUS = 0.1;
const STORAGE_BONUS = 0.15;
const CHAIN_BONUS = 0.16;
const CROWD_PENALTY = 0.045;
const CROWD_FREE = 2;
const CROWD_MAX = 0.27;

const MIN_MUL = 0.7;
const MAX_MUL = 1.62;

export class DistrictCache {
  /** Output multiplier per building slot. 1 until computed. */
  readonly mul = new Float32Array(MAX_BUILDINGS).fill(1);
  /** Bitfield of which terms fired, for the tooltip. */
  readonly why = new Uint8Array(MAX_BUILDINGS);
  /** Crowding neighbours counted, for the tooltip. */
  readonly crowd = new Uint8Array(MAX_BUILDINGS);
  /** -1 means "never computed"; forces a rebuild after a load. */
  stamp = -1;
}

export const DistrictWhy = {
  Road: 1 << 0,
  Storage: 1 << 1,
  Chain: 1 << 2,
  Crowded: 1 << 3,
} as const;

/**
 * How long a district score may lag the layout, in ticks.
 *
 * The pass is O(buildings²) — every producer is scored against every other
 * building in the town — and `dirty.districts` is set by every building that
 * finishes, is demolished or is *destroyed*. During a siege on a city of two
 * and a half thousand buildings that meant a six-million-pair sweep on very
 * nearly every tick, which was a tenth of the whole frame.
 *
 * A second and a half of lag costs nothing anybody can see: the score is a
 * production multiplier, and a sawmill whose neighbour burned down keeping its
 * old bonus for forty ticks is not a decision the player can even observe. What
 * it buys is that a siege knocking a quarter of the town down no longer
 * re-scores the whole town for each individual collapse.
 *
 * The lag is measured in ticks, so it is identical in every replay. A world
 * that has never computed its districts — a fresh run, or one just loaded —
 * computes immediately, because "not yet computed" is not staleness.
 */
const DISTRICT_REFRESH = 45;

/** Rebuild the cache if the layout has changed since the last pass. */
export function ensureDistricts(w: World): void {
  if (w.districts.stamp < 0) {
    recomputeDistricts(w);
    return;
  }
  if (!w.dirty.districts) return;
  if (w.tick - w.districts.stamp < DISTRICT_REFRESH) return;
  recomputeDistricts(w);
}

export function districtBonus(w: World, i: number): number {
  ensureDistricts(w);
  return w.districts.mul[i];
}

export function recomputeDistricts(w: World): void {
  const b = w.buildings;
  const d = w.districts;
  const map = w.map;

  // Gather the live player buildings once; everything below is a pass over this.
  const live: number[] = [];
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.team[i] !== Team.Player) continue;
    live.push(i);
  }

  // Precompute centres so the O(n²) passes are arithmetic rather than lookups.
  const cx = new Float64Array(live.length);
  const cy = new Float64Array(live.length);
  for (let n = 0; n < live.length; n++) {
    const i = live[n];
    const def = buildingType(b.typeId[i]);
    cx[n] = b.tx[i] + def.w / 2;
    cy[n] = b.ty[i] + def.h / 2;
  }

  for (let n = 0; n < live.length; n++) {
    const i = live[n];
    const def = buildingType(b.typeId[i]);

    // Only producers care. Everything else sits at 1 so the tooltip stays quiet.
    let produces = false;
    for (const k of RESOURCE_KEYS) {
      if ((def.produces[k] ?? 0) > 0) {
        produces = true;
        break;
      }
    }
    if (!produces) {
      d.mul[i] = 1;
      d.why[i] = 0;
      d.crowd[i] = 0;
      continue;
    }

    let mul = 1;
    let why = 0;

    if (touchesRoad(w, i)) {
      mul += ROAD_BONUS;
      why |= DistrictWhy.Road;
    }

    // Which inputs does this building want somebody nearby to be making?
    const wants: string[] = [];
    for (const k of RESOURCE_KEYS) {
      if ((def.consumes[k] ?? 0) > 0) wants.push(k);
    }

    let hasStorage = false;
    let fed = wants.length === 0;
    let crowd = 0;

    for (let m = 0; m < live.length; m++) {
      if (m === n) continue;
      const j = live[m];
      // A half-built neighbour is a promise, not a warehouse.
      if (b.build[j] < 1) continue;
      const dx = cx[m] - cx[n];
      const dy = cy[m] - cy[n];
      const dist2 = dx * dx + dy * dy;
      const other = buildingType(b.typeId[j]);

      if (!hasStorage && other.storage > 0 && dist2 <= STORAGE_REACH * STORAGE_REACH) {
        hasStorage = true;
      }
      if (!fed && dist2 <= CHAIN_REACH * CHAIN_REACH) {
        for (const k of wants) {
          if ((other.produces[k as (typeof RESOURCE_KEYS)[number]] ?? 0) > 0) {
            fed = true;
            break;
          }
        }
      }
      if (b.typeId[j] === b.typeId[i] && dist2 <= CROWD_REACH * CROWD_REACH) crowd++;
    }

    if (hasStorage) {
      mul += STORAGE_BONUS;
      why |= DistrictWhy.Storage;
    }
    if (fed && wants.length > 0) {
      mul += CHAIN_BONUS;
      why |= DistrictWhy.Chain;
    }
    const over = Math.max(0, crowd - CROWD_FREE);
    if (over > 0) {
      mul -= Math.min(CROWD_MAX, over * CROWD_PENALTY);
      why |= DistrictWhy.Crowded;
    }

    d.mul[i] = Math.max(MIN_MUL, Math.min(MAX_MUL, mul));
    d.why[i] = why;
    d.crowd[i] = Math.min(255, crowd);
  }

  // Anything not live keeps a neutral multiplier, so a recycled slot starts clean.
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 1 && b.team[i] === Team.Player) continue;
    d.mul[i] = 1;
    d.why[i] = 0;
    d.crowd[i] = 0;
  }

  d.stamp = w.tick;
  w.dirty.districts = false;

  void map;
}

/** Does any tile orthogonally touching the footprint carry a road? */
function touchesRoad(w: World, i: number): boolean {
  const b = w.buildings;
  const def = buildingType(b.typeId[i]);
  const x0 = b.tx[i];
  const y0 = b.ty[i];
  const map = w.map;
  for (let x = x0 - 1; x <= x0 + def.w; x++) {
    for (let y = y0 - 1; y <= y0 + def.h; y++) {
      // Corners do not count — a road has to actually run alongside.
      const onX = x >= x0 && x < x0 + def.w;
      const onY = y >= y0 && y < y0 + def.h;
      if (!onX && !onY) continue;
      if (onX && onY) continue;
      if (!map.inBounds(x, y)) continue;
      if (map.terrain[map.idx(x, y)] === Terrain.Road) return true;
    }
  }
  return false;
}

/** Human-readable reasons, for the building tooltip. */
export function districtReasons(w: World, i: number): string[] {
  ensureDistricts(w);
  const why = w.districts.why[i];
  const out: string[] = [];
  if (why & DistrictWhy.Road) out.push(`Road access +${Math.round(ROAD_BONUS * 100)}%`);
  if (why & DistrictWhy.Storage) out.push(`Storage nearby +${Math.round(STORAGE_BONUS * 100)}%`);
  if (why & DistrictWhy.Chain) out.push(`Supplier nearby +${Math.round(CHAIN_BONUS * 100)}%`);
  if (why & DistrictWhy.Crowded) {
    const over = Math.max(0, w.districts.crowd[i] - CROWD_FREE);
    out.push(`Crowded by ${w.districts.crowd[i]} of its kind −${Math.round(Math.min(CROWD_MAX, over * CROWD_PENALTY) * 100)}%`);
  }
  return out;
}
